<script setup>
import { computed, onMounted, onUnmounted, ref, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import CaptainPaywall from 'dashboard/components-next/captain/pageComponents/Paywall.vue';
import CatalogItemsPageEmptyState from 'dashboard/components-next/captain/pageComponents/emptyStates/CatalogItemsPageEmptyState.vue';
import CreateCatalogItemDialog from 'dashboard/components-next/captain/pageComponents/catalogItem/CreateCatalogItemDialog.vue';
import CatalogItemCard from 'dashboard/components-next/captain/pageComponents/catalogItem/CatalogItemCard.vue';
import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

// Set by the route: "Productos" and "Servicios" are the same screen over the same table,
// and the type is what keeps an admin from filing one as the other.
const props = defineProps({
  itemType: {
    type: String,
    default: 'product',
    validator: value => ['product', 'service'].includes(value),
  },
});

const store = useStore();
const { t } = useI18n();

const uiFlags = useMapGetter('captainCatalogItems/getUIFlags');
const catalogItems = useMapGetter('captainCatalogItems/getRecords');
const catalogItemsMeta = useMapGetter('captainCatalogItems/getMeta');
const limit = useMapGetter('captainCatalogItems/getLimit');
const isFetching = computed(() => uiFlags.value.fetchingList);

const typeKey = computed(() =>
  props.itemType === 'service' ? 'SERVICES' : 'PRODUCTS'
);

// Indexing an item is one embedding call, not a catalog download, so it is normally done
// within a second or two. The page still has to ask: nothing pushes the result back.
const INDEXING_POLL_INTERVAL = 4000;

const createDialogRef = ref(null);
const deleteDialogRef = ref(null);
const selectedItem = ref(null);
const dialogType = ref('');
const searchQuery = ref('');

const fetchCatalogItems = (page = 1) => {
  store.dispatch('captainCatalogItems/get', {
    page,
    itemType: props.itemType,
    searchKey: searchQuery.value || undefined,
  });
};

const pendingItemIds = computed(() =>
  catalogItems.value
    .filter(item => item.enabled && item.indexing?.status === 'pending')
    .map(item => item.id)
);

const pollPendingItems = () => {
  pendingItemIds.value.forEach(id => {
    // A failed refresh is the next tick's problem: stopping the poll over one would leave
    // the card frozen on whatever it last said.
    store.dispatch('captainCatalogItems/show', id).catch(() => {});
  });
};

let pollTimer = null;

const stopPolling = () => {
  if (!pollTimer) return;

  clearInterval(pollTimer);
  pollTimer = null;
};

watch(
  () => pendingItemIds.value.length,
  count => {
    if (count && !pollTimer) {
      pollTimer = setInterval(pollPendingItems, INDEXING_POLL_INTERVAL);
    } else if (!count) {
      stopPolling();
    }
  },
  // Immediate because the list can already be in the store when the page is opened a
  // second time, and then the count never changes for the watcher to notice.
  { immediate: true }
);

// Both screens share one store. Moving from one to the other must not keep showing the
// items of the screen just left, nor carry its search over.
watch(
  () => props.itemType,
  () => {
    searchQuery.value = '';
    fetchCatalogItems(1);
  }
);

// The ceiling is one number for the whole account, shared by products and services.
const hasReachedLimit = computed(
  () => Boolean(limit.value.totalCount) && limit.value.currentAvailable <= 0
);

const onPageChange = page => fetchCatalogItems(page);

const debouncedSearch = debounce(() => fetchCatalogItems(1), 500);

const openCreateDialog = () => {
  // Said here rather than letting the request come back with a 422: the ceiling is an
  // account setting, not a mistake the admin made filling in the form.
  if (hasReachedLimit.value) {
    useAlert(
      t('CAPTAIN.CATALOG_ITEMS.LIMIT.REACHED', {
        count: limit.value.totalCount,
      })
    );
    return;
  }

  dialogType.value = 'create';
  selectedItem.value = null;
  nextTick(() => createDialogRef.value.dialogRef.open());
};

const handleEdit = item => {
  dialogType.value = 'edit';
  selectedItem.value = item;
  nextTick(() => createDialogRef.value.dialogRef.open());
};

const handleDelete = item => {
  selectedItem.value = item;
  nextTick(() => deleteDialogRef.value.dialogRef.open());
};

const handleReindex = async item => {
  try {
    await store.dispatch('captainCatalogItems/reindex', item.id);
    // Reread it so the card picks up the pending mark, which is what starts the polling.
    await store.dispatch('captainCatalogItems/show', item.id);
    useAlert(t('CAPTAIN.CATALOG_ITEMS.REINDEX.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message || t('CAPTAIN.CATALOG_ITEMS.REINDEX.ERROR_MESSAGE'));
  }
};

const handleAction = async ({ action, id }) => {
  const item = catalogItems.value.find(record => record.id === id);
  if (!item) return;

  if (action === 'edit') {
    handleEdit(item);
  } else if (action === 'delete') {
    handleDelete(item);
  } else if (action === 'reindex') {
    await handleReindex(item);
  }
};

const handleDialogClose = () => {
  dialogType.value = '';
  selectedItem.value = null;
  // The count in the footer and the room left both move with a create, and neither is part
  // of the record the store just added.
  fetchCatalogItems(catalogItemsMeta.value.page || 1);
};

const onDeleteSuccess = () => {
  selectedItem.value = null;
  const page = catalogItemsMeta.value.page || 1;

  if (catalogItems.value.length === 1 && page > 1) {
    onPageChange(page - 1);
  } else {
    fetchCatalogItems(page);
  }
};

onMounted(() => {
  fetchCatalogItems();
});

onUnmounted(stopPolling);
</script>

<template>
  <PageLayout
    :header-title="$t(`CAPTAIN.CATALOG_ITEMS.${typeKey}.HEADER`)"
    :button-label="$t(`CAPTAIN.CATALOG_ITEMS.${typeKey}.ADD_NEW`)"
    :button-policy="['administrator']"
    :total-count="catalogItemsMeta.totalCount"
    :current-page="catalogItemsMeta.page"
    :show-pagination-footer="!isFetching && !!catalogItems.length"
    :is-fetching="isFetching"
    :is-empty="!catalogItems.length && !searchQuery"
    :feature-flag="FEATURE_FLAGS.CAPTAIN_V2"
    :show-know-more="false"
    @update:current-page="onPageChange"
    @click="openCreateDialog"
  >
    <template #paywall>
      <CaptainPaywall />
    </template>

    <template #search>
      <Input
        v-model="searchQuery"
        :placeholder="$t('CAPTAIN.CATALOG_ITEMS.SEARCH_PLACEHOLDER')"
        class="w-64"
        size="sm"
        type="search"
        @input="debouncedSearch"
      />
    </template>

    <template #controls>
      <div
        v-if="limit.totalCount"
        class="flex items-center gap-2 pb-3 text-sm"
        :class="hasReachedLimit ? 'text-n-amber-12' : 'text-n-slate-11'"
      >
        <span
          :class="
            hasReachedLimit ? 'i-lucide-triangle-alert' : 'i-lucide-package'
          "
          class="text-base shrink-0"
        />
        <span>
          {{
            $t('CAPTAIN.CATALOG_ITEMS.LIMIT.USAGE', {
              consumed: limit.consumed,
              total: limit.totalCount,
            })
          }}
        </span>
      </div>
    </template>

    <template #emptyState>
      <CatalogItemsPageEmptyState
        :item-type="itemType"
        @click="openCreateDialog"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <p
          v-if="!catalogItems.length"
          class="py-6 text-sm text-center text-n-slate-11"
        >
          {{ $t('CAPTAIN.CATALOG_ITEMS.NO_RESULTS') }}
        </p>
        <CatalogItemCard
          v-for="item in catalogItems"
          :id="item.id"
          :key="item.id"
          :name="item.name"
          :sku="item.sku"
          :description="item.description"
          :display-price="item.display_price"
          :enabled="item.enabled"
          :custom-fields="item.custom_fields"
          :media="item.media"
          :created-at="item.created_at"
          :updated-at="item.updated_at"
          :indexing="item.indexing"
          @action="handleAction"
        />
      </div>
    </template>
  </PageLayout>

  <CreateCatalogItemDialog
    v-if="dialogType"
    ref="createDialogRef"
    :type="dialogType"
    :item-type="itemType"
    :selected-item="selectedItem"
    @close="handleDialogClose"
  />

  <DeleteDialog
    v-if="selectedItem"
    ref="deleteDialogRef"
    :entity="selectedItem"
    type="CatalogItems"
    translation-key="CATALOG_ITEMS"
    @delete-success="onDeleteSuccess"
  />
</template>
